<template>
  <div class="container-fluid">
    <div class="row">
      <main id="nft-content" class="col" :class="{ 'active-sidebar': isSidebarOpen }">
        <div class="container" v-if="programData.id">
          <h1>
            <EditableCellInput v-model="programData.name" class="main-title" />
          </h1>
          <div class="row">
            <div class="col-2" />
            <div class="col-4 text-center">
              <h6>Objetivo</h6>
              <EditableCellSelect
                class="program-select"
                v-model="programData.objective"
                :units="objectivePresets"
                :nullable="false"
              />
            </div>
            <div class="col-4 text-center">
              <h6>Nivel</h6>
              <EditableCellSelect
                class="program-select"
                v-model="programData.level"
                :units="levelPresets"
                :nullable="false"
              />
            </div>
          </div>
          <ProgramIndications
            v-model="programData.indications"
            :presets="indicationPresets"
            class="indications-container"
          />
          <div class="routines-container">
            <transition-group name="routines">
              <ProgramRoutine
                v-for="(routine, index) in programData.routines"
                :key="'routine-'+routine.id"
                :index="index"
                :hiitPresets="hiitPresets"
                :hiitOptions="hiitOptions"
                v-model="programData.routines[index]"
                @remove="removeRoutine"
              />
            </transition-group>
          </div>
        </div>
        <SpinnerBackdrop v-else size="6rem" thickness="0.6rem" variant="info" />
      </main>
      <ProgramSidebar
        @open="openSidebar"
        @close="closeSidebar"
        @save-program="saveProgram"
        @get-program-file="getProgramFile"
        @add-routine="addRoutine"
        :program-data="programData"
        :unsaved-changes="unsavedChanges"
        :is-saving="isSaving"
        :is-exporting="isExporting"
      />
    </div>
    <ColorContextMenu
      :open="isColorMenuOpen"
      :colors="colorsHex"
      :swatches-per-row="9"
      @select-color="changeExerciseColor"
      @hover-color="changeExerciseColor"
      @reset-color="resetExerciseColor"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import { keyMixin } from "../mixins/keyMixin";
import ColorContextMenu from "../components/contextMenu/ColorContextMenu";
import EditableCellInput from "../components/EditableCellInput";
import EditableCellSelect from "../components/EditableCellSelect";
import patientService from "../services/patientService";
import ProgramIndications from "../components/ProgramIndications";
import ProgramRoutine from "../components/ProgramRoutine";
import ProgramSidebar from "../components/sidebar/ProgramSidebar";
import SpinnerBackdrop from "../components/SpinnerBackdrop";
import slugify from "slugify"

export default {
  name: "ProgramRoutinePage",
  components: {
    ColorContextMenu,
    EditableCellInput,
    EditableCellSelect,
    ProgramIndications,
    ProgramRoutine,
    ProgramSidebar,
    SpinnerBackdrop
  },
  mixins: [keyMixin],
  data: function() {
    return {
      isSidebarOpen: false,
      programData: {
        name: "",
        routines: {}
      },
      indicationPresets: [],
      colorPresets: [],
      levelPresets: [],
      objectivePresets: [],
      hiitPresets: [],
      hiitOptions: [],
      unsavedChanges: false,
      isSaving: false,
      isExporting: false
    };
  },
  computed: {
    colorsHex: function() {
      return Object.keys(this.colorPresets);
    },
    isColorMenuOpen: function() {
      return this.$store.state.contextMenu.open;
    },
    ...mapState(["login"])
  },
  mounted: function() {
    if (this.$store.state.login.token) {
      this.getProgram();
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.unsavedChanges) {
      const answer = window.confirm(
        "Tienes cambios sin guardar ¿Deseas salir?"
      );
      if (answer) {
        window.onbeforeunload = null;
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  methods: {
    openSidebar: function() {
      this.isSidebarOpen = true;
    },
    closeSidebar: function() {
      this.isSidebarOpen = false;
    },
    saveProgram: function() {
      this.isSaving = true;
      patientService
        .save_program({ id: this.$route.params.id, program: this.programData })
        .then(
          // eslint-disable-next-line
          data => {
            this.unsavedChanges = false;
            this.isSaving = false;
            this.$toasted.success("Guardado exitoso!");
          },
          // eslint-disable-next-line
          error => {
            this.isSaving = false;
            this.$toasted.error(
              "Ha ocurrido un error guardando el plan!"
            );
          }
        );
    },
    getProgram: function() {
      patientService.get_program({ id: this.$route.params.id }).then(
        data => {
          this.programData = data.program;
          this.indicationPresets = data.indicationPresets;
          this.colorPresets = data.colorPresets;
          this.levelPresets = data.levelPresets;
          this.objectivePresets = data.objectivePresets;
          this.hiitPresets = data.hiitPresets;
          this.hiitOptions = data.hiitOptions;
        },
        // eslint-disable-next-line
        error => {
          this.$toasted.error("Ha ocurrido un error obteniendo el plan!");
        }
      );
    },
    addRoutine: function() {
      this.programData.routines.push({
        id: this.random_key(),
        name: "Rutina " + (this.programData.routines.length + 1),
        days: [],
        exercises: [],
        cardio_start: null,
        cardio_hiit: null,
        cardio_end: null,
        has_exercises: true
      });
      this.$nextTick(() => {
        window.scroll({
          top:
            document.body.scrollHeight || document.documentElement.scrollHeight,
          left: 0,
          behavior: "smooth"
        });
      });
    },
    removeRoutine: function(routineIndex) {
      this.programData.routines.splice(routineIndex, 1);
    },
    changeExerciseColor: function(color) {
      this.$store.state.contextMenu.target.color = color;
    },
    resetExerciseColor: function() {
      this.$store.state.contextMenu.target.color = this.$store.state.contextMenu.targetOriginal.color;
    },
    getProgramFile: function() {
      this.isExporting = true;
      patientService
        .get_program_file({
          id: this.$route.params.id,
          program: this.programData
        })
        .then(
          data => {
            // create fake DOM element to download file
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement("a");
            link.href = url;
            let patientName = slugify(this.programData.patient.first_name + " " + this.programData.patient.last_name, "_")
            let programName = slugify(this.programData.name, "_")
            let fileName = programName + "_" + patientName + ".docx"
            link.setAttribute("download", fileName); //or any other extension
            document.body.appendChild(link);
            // click it to start download
            this.isExporting = false;
            this.unsavedChanges = false;
            link.click();
          },
          // eslint-disable-next-line
          error => {
            this.isExporting = false;
            this.$toasted.error("Ha ocurrido un error!");
          }
        );
    }
  },
  watch: {
    // eslint-disable-next-line
    "login.token": function(newValue, oldValue) {
      if (!newValue) {
        // eslint-disable-next-line
        this.$router.push({ name: "home" }).catch(err => {});
      }
    },
    "$route.params.id": function() {
      this.getProgram();
    },
    unsavedChanges(val) {
      window.onbeforeunload = val
        ? () => "Tienes cambios sin guardar ¿Deseas salir?"
        : null;
    },
    programData: {
      handler: function(val, oldVal) {
        if (oldVal.id) {
          this.unsavedChanges = true;
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped lang="scss">
#nft-content {
  padding-left: $content-padding-x;
  padding-right: $content-padding-x;
  padding-bottom: 10rem;
  min-width: 0;
  // transition: padding 0.3s;

  &.active-sidebar {
    padding-left: map-get($spacers, 2);
  }
}

.main-title {
  font-size: $h1-font-size;
  font-weight: bold;
  color: $body-color;
  margin: 2rem 0;
  text-align: center;
}

.add-indication-btn {
  margin: $spacer auto 0;
}

.indications-container {
  display: flex;
  flex-wrap: wrap;
  border: 1px solid $gray-400;
  border-radius: 10px;
  padding: 0 map_get($spacers, 4) map_get($spacers, 4);
  margin: $spacer 0 $spacer * 4;
}

.routines-container {
  margin: 2rem 0;
}

.routines-enter-active,
.routines-leave-active {
  transition: opacity 0.5s ease-in;
}

.routines-enter,
.routines-leave-to {
  opacity: 0;
}
.dropdown-menu.show {
  z-index: 1025;
}
.program-select {
  border: 1px solid grey;
}
</style>
<style lang="scss">
.indications-container .dropdown-menu.show {
  z-index: 1025;
}
</style>